import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Bell, BellOff, BellRing, Loader2, CheckCircle2 } from 'lucide-react';

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);

  // Check if push is supported
  useEffect(() => {
    const checkSupport = async () => {
      const supported = 'serviceWorker' in navigator && 
                       'PushManager' in window && 
                       'Notification' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
        
        // Check existing subscription
        try {
          const registration = await navigator.serviceWorker.ready;
          const existingSub = await registration.pushManager.getSubscription();
          if (existingSub) {
            setSubscription(existingSub);
            setIsSubscribed(true);
          }
        } catch (error) {
          console.error('Error checking subscription:', error);
        }
      }
    };
    
    checkSupport();
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      toast.error('Bu tarayıcı push bildirimlerini desteklemiyor');
      return false;
    }

    setLoading(true);

    try {
      // Request notification permission
      const permResult = await Notification.requestPermission();
      setPermission(permResult);
      
      if (permResult !== 'granted') {
        toast.error('Bildirim izni reddedildi');
        setLoading(false);
        return false;
      }

      // Get VAPID public key from backend
      const { data: vapidData } = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = urlBase64ToUint8Array(vapidData.publicKey);

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey
      });

      // Send subscription to backend
      const subscriptionJson = pushSubscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      });

      setSubscription(pushSubscription);
      setIsSubscribed(true);
      toast.success('Bildirimler etkinleştirildi! 🔔');
      setLoading(false);
      return true;

    } catch (error) {
      console.error('Push subscription error:', error);
      toast.error('Bildirim kaydı başarısız oldu');
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription) return false;

    setLoading(true);

    try {
      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Remove from backend
      const subscriptionJson = subscription.toJSON();
      await axios.delete(`${API}/push/unsubscribe?endpoint=${encodeURIComponent(subscriptionJson.endpoint)}`);

      setSubscription(null);
      setIsSubscribed(false);
      toast.success('Bildirimler kapatıldı');
      setLoading(false);
      return true;

    } catch (error) {
      console.error('Push unsubscribe error:', error);
      toast.error('Bildirim iptali başarısız');
      setLoading(false);
      return false;
    }
  }, [subscription]);

  // Send test notification
  const sendTest = useCallback(async () => {
    try {
      await axios.post(`${API}/push/test`);
      toast.success('Test bildirimi gönderildi');
    } catch (error) {
      toast.error('Test bildirimi gönderilemedi');
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe,
    sendTest
  };
}

// Push notification toggle button component
export function PushNotificationButton({ variant = 'default', size = 'default', showLabel = true }) {
  const { isSupported, isSubscribed, permission, loading, subscribe, unsubscribe } = usePushNotifications();

  if (!isSupported) {
    return null; // Don't show button if not supported
  }

  const handleClick = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  const getIcon = () => {
    if (loading) return <Loader2 className="w-4 h-4 animate-spin" />;
    if (isSubscribed) return <BellRing className="w-4 h-4" />;
    if (permission === 'denied') return <BellOff className="w-4 h-4" />;
    return <Bell className="w-4 h-4" />;
  };

  const getLabel = () => {
    if (loading) return 'İşleniyor...';
    if (isSubscribed) return 'Bildirimler Açık';
    if (permission === 'denied') return 'İzin Reddedildi';
    return 'Bildirimleri Aç';
  };

  const getVariantClass = () => {
    if (isSubscribed) return 'bg-emerald-500 hover:bg-emerald-600 text-white';
    if (permission === 'denied') return 'bg-gray-400 cursor-not-allowed';
    return '';
  };

  return (
    <Button
      variant={isSubscribed ? 'default' : variant}
      size={size}
      onClick={handleClick}
      disabled={loading || permission === 'denied'}
      className={`gap-2 ${getVariantClass()}`}
    >
      {getIcon()}
      {showLabel && <span>{getLabel()}</span>}
    </Button>
  );
}

// Compact icon-only button
export function PushNotificationIcon({ className = '' }) {
  const { isSupported, isSubscribed, loading, subscribe, unsubscribe } = usePushNotifications();

  if (!isSupported) return null;

  return (
    <button
      onClick={() => isSubscribed ? unsubscribe() : subscribe()}
      disabled={loading}
      className={`relative p-2 rounded-full transition-colors ${
        isSubscribed 
          ? 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100' 
          : 'text-gray-500 hover:bg-gray-100'
      } ${className}`}
      title={isSubscribed ? 'Bildirimleri Kapat' : 'Bildirimleri Aç'}
    >
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : isSubscribed ? (
        <>
          <BellRing className="w-5 h-5" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-emerald-500 rounded-full" />
        </>
      ) : (
        <Bell className="w-5 h-5" />
      )}
    </button>
  );
}

// Settings panel for push notifications
export function PushNotificationSettings() {
  const { isSupported, isSubscribed, permission, loading, subscribe, unsubscribe, sendTest } = usePushNotifications();

  if (!isSupported) {
    return (
      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <div className="flex items-center gap-2 text-yellow-800">
          <BellOff className="w-5 h-5" />
          <span className="font-medium">Push bildirimleri desteklenmiyor</span>
        </div>
        <p className="text-sm text-yellow-700 mt-1">
          Bu özellik için uygulamayı ana ekrana eklemeniz gerekiyor (iOS 16.4+).
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white rounded-lg border space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-gray-900">Push Bildirimleri</h4>
          <p className="text-sm text-gray-500">
            {isSubscribed 
              ? 'Anlık bildirimler aktif' 
              : 'Anlık bildirimleri etkinleştirin'}
          </p>
        </div>
        <PushNotificationButton showLabel={false} variant="outline" />
      </div>

      {isSubscribed && (
        <div className="flex items-center justify-between pt-2 border-t">
          <span className="text-sm text-gray-600">Test bildirimi gönder</span>
          <Button variant="outline" size="sm" onClick={sendTest}>
            Test
          </Button>
        </div>
      )}

      {permission === 'denied' && (
        <div className="p-3 bg-red-50 rounded-lg">
          <p className="text-sm text-red-700">
            Bildirim izni reddedildi. Tarayıcı ayarlarından izin vermeniz gerekiyor.
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-gray-400">
        <CheckCircle2 className="w-3 h-3" />
        <span>iOS 16.4+ ve PWA desteği ile çalışır</span>
      </div>
    </div>
  );
}

export default PushNotificationButton;
